"use client"

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CaptchaDialog as Dialog, CaptchaDialogContent as DialogContent } from '@/components/SmartCaptchaDialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { buildLeadThankYouUrl, clearPendingLeadSubmissionId, extractOptionalLeadId, getOrCreatePendingLeadSubmissionId } from '@/lib/forms/lead-thank-you';
import { cn } from '@/lib/utils';
import { useSmartCaptcha } from '@/components/SmartCaptcha';

const FORM_TYPE = 'call-to-action';

interface CallToActionProps {
  className?: string;
  title?: string;
  description?: string;
  buttonText?: string;
  buttonLink?: string;
  icon?: React.ReactNode;
}

export default function CallToAction({
  className,
  title = 'Оформите заявку на сайте, мы свяжемся с вами в ближайшее время и ответим на интересующие вопросы',
  description,
  buttonText = 'Заказать услугу',
  buttonLink = '/contact',
  icon,
}: CallToActionProps) {
  const router = useRouter();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    privacy: false,
    marketing: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const { captchaElement, executeCaptcha, resetCaptcha, isCaptchaReady, challengeVisible } = useSmartCaptcha();

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Проверка обязательных полей
    if (!formData.privacy) {
      setSubmitStatus('error');
      setSubmitMessage('Необходимо согласие на обработку персональных данных');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitMessage('');
    
    try {
      const submissionId = getOrCreatePendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
      const smartToken = await executeCaptcha();
      const response = await fetch('/api/forms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          privacy: formData.privacy,
          marketing: formData.marketing,
          formType: FORM_TYPE,
          pageUrl: typeof window !== 'undefined' ? window.location.href : '',
          smartToken,
          submissionId,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const thankYouUrl = buildLeadThankYouUrl({
          pathname: window.location.pathname,
          search: window.location.search,
          formType: FORM_TYPE,
          leadId: extractOptionalLeadId(data),
          sid: submissionId,
        });

        clearPendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
        router.push(thankYouUrl);
        return;
      } else {
        setSubmitStatus('error');
        setSubmitMessage(data.message || 'Ошибка при отправке заявки');
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('Произошла ошибка. Попробуйте позже.');
      console.error('Ошибка при отправке формы:', error);
    } finally {
      resetCaptcha();
      setIsSubmitting(false);
    }
  };

  return (
    <section className={cn('py-4 sm:py-8', className)}>
      <div className="w-full">
        <div className="bg-[#d9d9d9] rounded-[15px] px-6 sm:px-[59px] py-6 sm:py-[30px] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 sm:gap-[45px]">
          {/* Left side: Icon and Text */}
          <div className="flex items-start sm:items-center gap-6 sm:gap-[45px] flex-1">
            {/* Icon Circle */}
            <div className="flex-shrink-0 w-[45px] h-[45px] rounded-full bg-[#626262] flex items-center justify-center">
              {icon || (
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                  />
                </svg>
              )}
            </div>

            {/* Text Content */}
            <div className="flex-1">
              {description ? (
                <>
                  {title && (
                    <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-2">
                      {title}
                    </h3>
                  )}
                  <p className="text-sm sm:text-base sm:text-lg text-slate-900 leading-relaxed">
                    {description}
                  </p>
                </>
              ) : (
                <p className="text-sm sm:text-base sm:text-lg text-slate-900 leading-relaxed">
                  {title}
                </p>
              )}
            </div>
          </div>

          {/* Right side: Button */}
          <div className="flex-shrink-0 w-full sm:w-auto">
            <Button
              onClick={() => setIsModalOpen(true)}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-8 sm:px-[50px] py-2.5 sm:py-[11px] rounded-lg text-base sm:text-base font-normal h-auto"
            >
              {buttonText}
            </Button>
          </div>
        </div>
      </div>

      {/* Модальное окно с формой консультации */}
      <Dialog challengeVisible={challengeVisible} busy={isSubmitting} open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Заказать консультацию</DialogTitle>
            <DialogDescription>
              {description || 'Заполните форму, и мы свяжемся с вами в ближайшее время для консультации.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleFormSubmit} className="mt-4 space-y-4">
            <div>
              <Label htmlFor="consultation-name" className="text-sm font-medium text-gray-700">
                ФИО <span className="text-red-500">*</span>
              </Label>
              <Input
                id="consultation-name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleFormChange}
                className="mt-2"
                placeholder="Иванов Иван Иванович"
              />
            </div>
            <div>
              <Label htmlFor="consultation-phone" className="text-sm font-medium text-gray-700">
                Телефон <span className="text-red-500">*</span>
              </Label>
              <Input
                id="consultation-phone"
                name="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={handleFormChange}
                className="mt-2"
                placeholder="+7 (999) 123-45-67"
              />
            </div>
            <div>
              <Label htmlFor="consultation-email" className="text-sm font-medium text-gray-700">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="consultation-email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleFormChange}
                className="mt-2"
                placeholder="example@mail.com"
              />
            </div>
            <div>
              <Label htmlFor="consultation-message" className="text-sm font-medium text-gray-700">
                Дополнительная информация
              </Label>
              <textarea
                id="consultation-message"
                name="message"
                value={formData.message}
                onChange={handleFormChange}
                className="mt-2 flex min-h-[100px] w-full rounded-md border border-slate-200 bg-transparent px-3 py-2 text-base shadow-sm transition-colors placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:border-slate-800 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300"
                placeholder="Опишите ваш вопрос или что вас интересует"
                rows={4}
              />
            </div>

            {/* Чекбоксы согласия */}
            <div className="space-y-3">
              <div className="flex items-start">
                <Checkbox
                  id="consultation-privacy"
                  checked={formData.privacy}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, privacy: checked as boolean }))}
                  required
                  className="mt-1"
                />
                <Label htmlFor="consultation-privacy" className="ml-2 text-sm text-gray-600">
                  Я даю согласие на обработку своих персональных данных и соглашаюсь с{' '}
                  <a href="/pages/politika-konfidentsialnosti-personalnykh-dannykh/" className="text-blue-600 hover:underline">
                    политикой конфиденциальности
                  </a>
                  . <span className="text-red-500">*</span>
                </Label>
              </div>

              <div className="flex items-start">
                <Checkbox
                  id="consultation-marketing"
                  checked={formData.marketing}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, marketing: checked as boolean }))}
                  className="mt-1"
                />
                <Label htmlFor="consultation-marketing" className="ml-2 text-sm text-gray-600">
                  Я даю согласие на направление рекламных рассылок.
                </Label>
              </div>
            </div>

            {/* Сообщения о результате */}
            {submitStatus === 'success' && (
              <div className="flex items-center gap-2 rounded-lg bg-green-50 p-4 text-green-800">
                <CheckCircle2 className="h-5 w-5 shrink-0" />
                <p className="text-sm font-medium">{submitMessage}</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-red-800">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <p className="text-sm font-medium">{submitMessage}</p>
              </div>
            )}

            {captchaElement}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                disabled={isSubmitting}
              >
                Отмена
              </Button>
              <Button
                type="submit"
                variant="default"
                disabled={!isCaptchaReady || isSubmitting || !formData.privacy}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
